/**
 * Persian numeral and separator handling (design-spec.md §7).
 *
 * Every numeral the user sees is Persian (۰۱۲۳۴۵۶۷۸۹) and the thousands
 * separator is `٬` (U+066C ARABIC THOUSANDS SEPARATOR). Anything the user
 * types comes back the other way before it reaches the API.
 */

const PERSIAN_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'] as const
const ARABIC_DIGITS = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'] as const

export const THOUSANDS_SEP = '٬'
export const DECIMAL_SEP = '٫'

/** Latin digits → Persian. Leaves every other character untouched. */
export function toPersianDigits(input: string): string {
  return input.replace(/\d/g, (d) => PERSIAN_DIGITS[Number(d)] as string)
}

/**
 * Persian *and* Arabic-Indic digits → Latin, and Persian separators → ASCII.
 * Use on everything the user types before parsing or sending it.
 */
export function toLatinDigits(input: string): string {
  let out = ''
  for (const ch of input) {
    const p = PERSIAN_DIGITS.indexOf(ch as (typeof PERSIAN_DIGITS)[number])
    if (p >= 0) {
      out += String(p)
      continue
    }
    const a = ARABIC_DIGITS.indexOf(ch as (typeof ARABIC_DIGITS)[number])
    if (a >= 0) {
      out += String(a)
      continue
    }
    if (ch === THOUSANDS_SEP || ch === ',') continue
    out += ch === DECIMAL_SEP ? '.' : ch
  }
  return out
}

/** Group an integer digit string in threes: `2000000` → `2٬000٬000`. */
export function groupThousands(digits: string, separator = THOUSANDS_SEP): string {
  const negative = digits.startsWith('-')
  const body = negative ? digits.slice(1) : digits
  const grouped = body.replace(/\B(?=(\d{3})+(?!\d))/g, separator)
  return negative ? `-${grouped}` : grouped
}

/** Strip everything that is not a digit — for OTP and numeric-only fields. */
export function digitsOnly(input: string): string {
  return toLatinDigits(input).replace(/\D/g, '')
}
